import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { Physics } from '../physics/Physics';
import type { World, CollectibleInstance } from '../world/World';
import type { Scoring, CollectResult } from '../scoring/Scoring';
import type { SurfaceMaterial } from '../world/Surfaces';
import { BALL_RADIUS } from './Panda';
import { clamp } from '../util/math';

export interface ControllerEvents {
  onJump: () => void;
  onLand: (intensity: number) => void;
  onBounce: (pos: THREE.Vector3) => void;
  onPickup: (item: CollectibleInstance, result: CollectResult) => void;
  onObstacle: (pos: THREE.Vector3) => void;
  onBamboo: (pos: THREE.Vector3) => void;
  onFall: (pos: THREE.Vector3) => void;
  onFinish: () => void;
  onSurface: (surf: SurfaceMaterial) => void;
}

export interface ControllerState {
  pos: THREE.Vector3;
  quat: THREE.Quaternion;
  velocity: THREE.Vector3;
  speed: number;
  speed01: number;
  grounded: boolean;
  airborne: boolean;
  t: number;
  surface: SurfaceMaterial;
  steerAuthority: number;
  finished: boolean;
  respawning: boolean;
}

// ---- Tunables (§2/§12) -----------------------------------------------------
// Tuned gentle & kid-friendly: slower top speed, steering that stays responsive
// even at speed, so 7-year-olds can keep it on the track.
const MAX_SPEED = 28; // reference top speed for normalization & FX
// Steering stays responsive at any speed — going fast is hard because there's
// less time to react before the next thing, NOT because the controls go numb.
// The tiny falloff below just takes the edge off twitchiness at full tilt.
const MAX_STEER_SPEED = 90; // speed at which authority bottoms out (very gentle)
const MIN_AUTHORITY = 0.88; // floor so steering stays crisp even at top speed
const BASE_AUTHORITY = 36; // lateral steering acceleration (between old & snappy)
const AIR_CONTROL = 0.2; // minimal air steer nudge (§2)
const BASE_TARGET_SPEED = 10; // pace target at the top of the hill
const TARGET_SPEED_RAMP = 7; // added across the run -> a bit faster by the bottom
const PACE_ACCEL = 11; // how hard we push toward the pace target
const SPEED_CAP_K = 7; // how hard we scrub above the surface's max
const BASE_DAMPING = 0.15; // baseline linear damping (scaled by friction)
const JUMP_SPEED = 12.5;
const JUMP_MIN_FORWARD = 10; // a jump always hops forward, so it clears obstacles you're pressed against
const BOUNCE_SPEED = 21;
const BOUNCE_MIN_FORWARD = 16; // ensure a bounce always carries you forward (clears chasms)
const OBSTACLE_COOLDOWN = 0.7; // min seconds between obstacle penalties (debounce repeat hits)
const RESPAWN_TIME = 0.9;
const KILL_DROP = 6; // height below surface that counts as a fall

const WORLD_UP = new THREE.Vector3(0, 1, 0);

export class PandaController {
  body!: RAPIER.RigidBody;
  collider!: RAPIER.Collider;

  private sampleHint = -1;
  private bouncePadCooldown = 0;
  private obstacleCooldown = 0;
  private respawnTimer = 0;
  private wasGrounded = true;
  private airTime = 0;
  private lastSafeT = 0;
  private lastRespawnT = -1;
  private consecutiveFalls = 0;
  private lastSurfaceId = '';
  private finished = false;

  state: ControllerState;

  constructor(
    private physics: Physics,
    private world: World,
    private scoring: Scoring,
    private events: ControllerEvents,
  ) {
    const RAPIER = physics.RAPIER;
    const start = world.startFrame();
    const startPos = start.pos.clone().addScaledVector(start.up, BALL_RADIUS + 0.2);

    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(startPos.x, startPos.y, startPos.z)
      .setLinearDamping(BASE_DAMPING)
      .setAngularDamping(0.4)
      .setCcdEnabled(true);
    this.body = physics.world.createRigidBody(bodyDesc);
    const colDesc = RAPIER.ColliderDesc.ball(BALL_RADIUS)
      .setDensity(1.0)
      .setFriction(1.0)
      .setRestitution(0.2);
    this.collider = physics.world.createCollider(colDesc, this.body);

    this.lastSafeT = 0;
    this.state = {
      pos: startPos.clone(),
      quat: new THREE.Quaternion(),
      velocity: new THREE.Vector3(),
      speed: 0,
      speed01: 0,
      grounded: true,
      airborne: false,
      t: 0,
      surface: world.getSurfaceAt(0, 0),
      steerAuthority: 1,
      finished: false,
      respawning: false,
    };
  }

  spawnAtStart(): void {
    const start = this.world.startFrame();
    const p = start.pos.clone().addScaledVector(start.up, BALL_RADIUS + 0.2);
    this.teleport(p);
    this.lastSafeT = 0;
    this.lastRespawnT = -1;
    this.consecutiveFalls = 0;
    this.finished = false;
    this.respawnTimer = 0;
    this.obstacleCooldown = 0;
    this.airTime = 0;
    this.sampleHint = -1;
    // Refresh state immediately so the camera snap/clamp use the start position
    // (controller.update only runs once play begins).
    this.state = {
      ...this.state,
      pos: p.clone(),
      quat: new THREE.Quaternion(),
      velocity: new THREE.Vector3(),
      speed: 0,
      speed01: 0,
      grounded: true,
      airborne: false,
      t: 0,
      surface: this.world.getSurfaceAt(0, 0),
      finished: false,
      respawning: false,
    };
  }

  private teleport(p: THREE.Vector3): void {
    this.body.setTranslation({ x: p.x, y: p.y, z: p.z }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
  }

  private respawn(): void {
    // Count rapid repeats: falling again without progressing past the last
    // respawn means that spot itself is the problem — retreat further back up
    // the centerline each time instead of dropping on the same bad ledge.
    if (this.lastRespawnT >= 0 && this.state.t <= this.lastRespawnT + 0.01) {
      this.consecutiveFalls += 1;
    } else {
      this.consecutiveFalls = 0;
    }
    // Centerline respawn (middle of the track); far side of any chasm fallen into.
    const r = this.world.respawnFor(this.state.t, this.lastSafeT, this.consecutiveFalls);
    this.lastRespawnT = r.frame.t;
    this.teleport(r.pos);
    this.respawnTimer = RESPAWN_TIME;
    this.scoring.breakCombo();
    this.events.onFall(r.pos.clone());
  }

  private isGrounded(pos: THREE.Vector3, up: THREE.Vector3): boolean {
    const RAPIER = this.physics.RAPIER;
    const origin = { x: pos.x, y: pos.y, z: pos.z };
    const dir = { x: -up.x, y: -up.y, z: -up.z };
    const ray = new RAPIER.Ray(origin, dir);
    const hit = this.physics.world.castRay(
      ray,
      BALL_RADIUS + 0.35,
      true,
      undefined,
      undefined,
      undefined,
      this.body,
    );
    return hit !== null;
  }

  /** Called by Game when a collision event involves an obstacle collider. */
  handleObstacleContact(): void {
    if (this.respawnTimer > 0) return;
    // Throttle: ignore rapid repeat hits (bouncing off the same wall/log) so a
    // single bump doesn't grind you to a stop or drain mood several times over.
    if (this.obstacleCooldown > 0) return;
    this.obstacleCooldown = OBSTACLE_COOLDOWN;
    // Scrub speed and break the combo (§5). Rapier already bounced us.
    const v = this.body.linvel();
    const speed = Math.hypot(v.x, v.y, v.z);
    if (speed > 4) {
      const k = 0.35;
      this.body.setLinvel({ x: v.x * (1 - k), y: v.y, z: v.z * (1 - k) }, true);
    }
    this.scoring.breakCombo();
    this.events.onObstacle(this.state.pos.clone());
  }

  update(dt: number, input: { steer: number; jump: boolean }): ControllerState {
    const mass = this.body.mass() || 1;
    const tr = this.body.translation();
    const pos = new THREE.Vector3(tr.x, tr.y, tr.z);
    const rot = this.body.rotation();
    const quat = new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w);
    const lv = this.body.linvel();
    const velocity = new THREE.Vector3(lv.x, lv.y, lv.z);

    const proj = this.world.spline.project(pos, this.sampleHint);
    this.sampleHint = proj.sampleIndex;
    const frame = proj.frame;
    const surface = this.world.getSurfaceAt(proj.t, proj.lateralNorm);

    const grounded = this.isGrounded(pos, frame.up);
    const airborne = !grounded;

    const speed = velocity.length();
    const speed01 = clamp(speed / MAX_SPEED, 0, 1);

    // Surface change cue.
    if (surface.id !== this.lastSurfaceId) {
      this.lastSurfaceId = surface.id;
      this.events.onSurface(surface);
    }

    if (this.respawnTimer > 0) this.respawnTimer -= dt;
    const respawning = this.respawnTimer > 0;

    // --- Steering: authority drops with speed (§2 core) ---------------------
    const authorityFactor = clamp(1 - speed / MAX_STEER_SPEED, MIN_AUTHORITY, 1);
    const steerAuthority = authorityFactor * surface.gripMultiplier;
    let steerAccel = BASE_AUTHORITY * steerAuthority;
    if (airborne) steerAccel *= AIR_CONTROL;
    if (respawning) steerAccel *= 0.3;
    const steerImpulse = frame.right.clone().multiplyScalar(input.steer * steerAccel * mass * dt);
    this.body.applyImpulse({ x: steerImpulse.x, y: steerImpulse.y, z: steerImpulse.z }, true);

    // --- Pace: gravity does the base work; a gentle assist ramps the pace ---
    if (grounded && !respawning) {
      const downhill = frame.tangent.clone();
      downhill.y = Math.min(downhill.y, 0); // only push downhill, never uphill
      if (downhill.lengthSq() > 1e-4) {
        downhill.normalize();
        const targetSpeed = (BASE_TARGET_SPEED + TARGET_SPEED_RAMP * proj.t) * surface.maxSpeedMultiplier;
        if (speed < targetSpeed) {
          const assist = PACE_ACCEL * surface.maxSpeedMultiplier;
          const imp = downhill.multiplyScalar(assist * mass * dt);
          this.body.applyImpulse({ x: imp.x, y: imp.y, z: imp.z }, true);
        } else {
          // Scrub above the surface's max (realizes maxSpeedMultiplier).
          const over = speed - targetSpeed;
          const dir = velocity.clone().normalize();
          const imp = dir.multiplyScalar(-over * SPEED_CAP_K * mass * dt);
          this.body.applyImpulse({ x: imp.x, y: imp.y, z: imp.z }, true);
        }
      }
    }

    // --- Surface friction (sticky mud vs slick ice) ------------------------
    this.body.setLinearDamping(BASE_DAMPING * surface.frictionMultiplier);

    // --- Jump --------------------------------------------------------------
    if (input.jump && grounded && !respawning) {
      const up = WORLD_UP.clone().lerp(frame.up, 0.5).normalize();
      const imp = up.multiplyScalar(JUMP_SPEED * mass);
      this.body.applyImpulse({ x: imp.x, y: imp.y, z: imp.z }, true);
      // Forward hop assist: if you're slow/stopped (e.g. pressed against a log
      // or wall), carry forward so the jump clears it instead of dropping back
      // on the same spot. Fast rolls are unaffected (already above the floor).
      const fwd = frame.tangent.clone();
      fwd.y = 0;
      if (fwd.lengthSq() > 1e-4) {
        fwd.normalize();
        const curFwd = velocity.dot(fwd);
        if (curFwd < JUMP_MIN_FORWARD) {
          const add = (JUMP_MIN_FORWARD - curFwd) * mass;
          this.body.applyImpulse({ x: fwd.x * add, y: 0, z: fwd.z * add }, true);
        }
      }
      this.events.onJump();
    }

    if (this.obstacleCooldown > 0) this.obstacleCooldown -= dt;

    // --- Bounce pads -------------------------------------------------------
    if (this.bouncePadCooldown > 0) this.bouncePadCooldown -= dt;
    if (this.bouncePadCooldown <= 0) {
      for (const pad of this.world.bouncePads) {
        if (pos.distanceTo(pad.pos) < pad.radius + BALL_RADIUS) {
          const imp = WORLD_UP.clone().multiplyScalar(BOUNCE_SPEED * mass);
          this.body.applyImpulse({ x: imp.x, y: imp.y, z: imp.z }, true);
          // Guarantee forward momentum so a slow approach still arcs across a
          // chasm instead of dropping straight back down into it.
          const fwd = frame.tangent.clone();
          fwd.y = 0;
          if (fwd.lengthSq() > 1e-4) {
            fwd.normalize();
            const curFwd = velocity.dot(fwd);
            if (curFwd < BOUNCE_MIN_FORWARD) {
              const add = (BOUNCE_MIN_FORWARD - curFwd) * mass;
              this.body.applyImpulse({ x: fwd.x * add, y: 0, z: fwd.z * add }, true);
            }
          }
          this.bouncePadCooldown = 0.5;
          this.events.onBounce(pad.pos.clone());
          break;
        }
      }
    }

    // --- Collectibles (contactless-on-touch) -------------------------------
    for (const item of this.world.collectibles) {
      if (item.collected) continue;
      if (pos.distanceTo(item.pos) < item.radius + BALL_RADIUS) {
        this.world.collect(item);
        const result = this.scoring.collect(item.tier);
        this.events.onPickup(item, result);
      }
    }

    // --- Bamboo: snap off + a slight slowdown (no combo break) --------------
    // Stalks are tall and their stored position is up the stalk, so test the
    // horizontal distance with a loose vertical tolerance (not 3D distance).
    for (const stalk of this.world.bambooStalks) {
      if (stalk.snapped) continue;
      const dx = pos.x - stalk.pos.x;
      const dz = pos.z - stalk.pos.z;
      const dy = pos.y - stalk.pos.y;
      if (Math.hypot(dx, dz) < stalk.radius + BALL_RADIUS && Math.abs(dy) < 4) {
        this.world.snapBamboo(stalk, velocity);
        const v = this.body.linvel();
        this.body.setLinvel({ x: v.x * 0.9, y: v.y, z: v.z * 0.9 }, true);
        this.events.onBamboo(stalk.pos.clone());
      }
    }

    // --- Airtime tracking + landing squash ---------------------------------
    if (airborne) {
      this.airTime += dt;
    }
    if (grounded && !this.wasGrounded) {
      const impactSpeed = Math.abs(velocity.dot(frame.up));
      this.events.onLand(clamp(impactSpeed / 14, 0.1, 1));
      this.airTime = 0;
    }
    this.wasGrounded = grounded;

    // --- Off-track / fall reset (§5) ---------------------------------------
    // A fall = dropping well below the surface (off a cliff or into a chasm).
    // The raised grassy sides keep you on everywhere else, so lateral alone
    // never triggers a reset (rolling up a hill is fine).
    if (!this.finished && (proj.height < -KILL_DROP || Math.abs(proj.lateralNorm) > 3.2)) {
      this.respawn();
    } else if (
      grounded &&
      Math.abs(proj.lateralNorm) < 0.85 &&
      proj.height > -1.5 &&
      !respawning &&
      this.world.isSafeRespawnT(proj.t)
    ) {
      // Remember the last solidly-centered, on-track progress to respawn at —
      // but never a narrow bridge (isSafeRespawnT), so we never pick a spot that
      // would just slide us off again.
      this.lastSafeT = proj.t;
    }

    // --- Safety: never get stuck outside the containment wall --------------
    // The wall blocks rolling back in if a big bounce carried us over it, so as
    // a last resort nudge back to just inside the wall (a small correction, not
    // all the way to center), keeping forward speed. This should rarely fire
    // (gaps sit on straight sections). height > 0.3 = on a hill, not a cliff, so
    // intended falls are untouched.
    if (grounded && !respawning && Math.abs(proj.lateralNorm) > 1.22 && proj.height > 0.3) {
      const insideLN = Math.sign(proj.lateralNorm) * 0.92; // just inside the wall
      const target = frame.pos
        .clone()
        .addScaledVector(frame.right, insideLN * frame.halfWidth)
        .addScaledVector(frame.up, BALL_RADIUS + 0.2);
      this.body.setTranslation({ x: target.x, y: target.y, z: target.z }, true);
      const fwd = frame.tangent.clone();
      const sp = velocity.dot(fwd);
      this.body.setLinvel({ x: fwd.x * sp, y: velocity.y, z: fwd.z * sp }, true);
    }

    // --- Finish ------------------------------------------------------------
    if (!this.finished && proj.t >= this.world.finishT - 0.002) {
      this.finished = true;
      this.events.onFinish();
    }

    // Clamp insane angular velocity.
    const av = this.body.angvel();
    const avMag = Math.hypot(av.x, av.y, av.z);
    if (avMag > 60) {
      const s = 60 / avMag;
      this.body.setAngvel({ x: av.x * s, y: av.y * s, z: av.z * s }, true);
    }

    this.state = {
      pos,
      quat,
      velocity,
      speed,
      speed01,
      grounded,
      airborne: airborne && this.airTime > 0.08,
      t: proj.t,
      surface,
      steerAuthority,
      finished: this.finished,
      respawning,
    };
    return this.state;
  }
}
